import { getAxiosInstance, withSuppressedErrorToast } from '@/client/get-axios-instance';
import { ChatMessage } from '@/contracts/marketplace/chat-message';
import { ReviewsPageGroupsResponse } from '@/contracts/marketplace/reviews-page-groups';
import { ReviewAnswerDto } from '@/contracts/marketplace/reviews';
import { MarketplaceReviewSelectionDetails } from '@/contracts/marketplace/review-records';
import { AibackReportPdfPayloadEntity, AibackReportSummaryEntity } from '@/entities/aiback-report.entity';
import { ReviewAnswerTemplateEntity } from '@/entities/review-answer-template.entity';
import { ReviewEntity } from '@/entities/review.entity';
import { buildMarketplaceApiPath } from '@/repositories/marketplace-api-paths';
import {
    AnswerTemplateUpsertPayload,
    ApproveReviewAnswerDraftPayload,
    ApproveReviewAnswerDraftResponse,
    AutoReplyScenarioEntity,
    AutoReplyScenarioPayload,
    CreateReviewAnswersPayload,
    CreateReviewAnswersResponse,
    AiSelectedAnalysisProgressSnapshotResponse,
    DownloadCsvQuery,
    DownloadCsvResult,
    EnqueueAiSelectedAnalysisPayload,
    EnqueueAiSelectedAnalysisResponse,
    GenerateAiAnswerPayload,
    GenerateManualAiAnswerPayload,
    GenerateAiAnswerOptions,
    GenerateAiAnswerResponse,
    GetReportSummaryOptions,
    GetReviewIdsForSelectionQuery,
    GetReviewsQuery,
    ReviewAnswerDraftEntity,
    ReviewIdRow,
    ReviewsReportSummaryQuery,
    SelectedAutoRepliesRunPayload,
    SelectedAutoRepliesRunResponse,
} from '@/repositories/marketplace-reviews.repository/marketplace-reviews.types';
import { extractSelectionLimitWarning, triggerBlobDownload } from '@/repositories/csv-download';
import { createRepositoryApiErrorFromResponse, normalizeRepositoryError } from '@/repositories/repository-api-error';
import { objectToUrlParams } from '@/shared/object-to-url-params';
import { EndpointOptions } from '@/types/endpoint.options';
import { PaginatedResponse } from '@/types/paginated.response';
import { Sort } from '@/types/sort.type';
import { AxiosResponse } from 'axios';
import {
    parseUnsupportedMarketplaceAxiosBlobResponse,
    UnsupportedMarketplaceRouteError,
} from '@/repositories/unsupported-marketplace-error';

export class MarketplaceReviewsTransportClient {
    private readonly _axios: ReturnType<typeof getAxiosInstance>;

    constructor() {
        this._axios = getAxiosInstance(buildMarketplaceApiPath('reviews'));
    }

    private requestOptions(options?: EndpointOptions) {
        return {
            headers: { Authorization: options?.authToken },
            signal: options?.signal,
        };
    }

    private suppressedRequestOptions(options?: EndpointOptions) {
        return withSuppressedErrorToast(this.requestOptions(options));
    }

    private toQueryString(params: URLSearchParams | Record<string, unknown>) {
        if (params instanceof URLSearchParams) {
            return params.toString();
        }

        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (Array.isArray(value)) {
                value.forEach((item) => searchParams.append(key, String(item)));
                continue;
            }

            if (value) {
                searchParams.set(key, String(value));
            }
        }

        return searchParams.toString();
    }

    getReview(id: string, options?: EndpointOptions) {
        return this._axios.get<ReviewEntity>(`/${id}`, this.requestOptions(options));
    }

    getReviews(query: GetReviewsQuery, options?: EndpointOptions) {
        return this._axios.get<PaginatedResponse<ReviewEntity>>('', {
            params: objectToUrlParams(query),
            ...this.requestOptions(options),
        });
    }

    getReviewsPageGroups(options?: EndpointOptions) {
        return this._axios.get<ReviewsPageGroupsResponse>('/page-groups', this.requestOptions(options));
    }

    getReportSummary(query?: ReviewsReportSummaryQuery, options?: EndpointOptions) {
        return this._axios.get<AibackReportSummaryEntity>('/report-summary', {
            params: objectToUrlParams(query || {}),
            ...this.requestOptions(options),
        });
    }

    async startReportSummaryStream(
        query?: ReviewsReportSummaryQuery,
        options?: GetReportSummaryOptions,
    ): Promise<Response> {
        const headers: Record<string, string> = {};
        if (options?.authToken) {
            headers.Authorization = options.authToken;
        }

        const queryParams = objectToUrlParams({
            ...(query || {}),
            stream: '1',
        });

        return await fetch(`${buildMarketplaceApiPath('reviews/report-summary')}?${this.toQueryString(queryParams)}`, {
            method: 'GET',
            headers,
            signal: options?.signal,
        });
    }

    async getReviewIdsForSelection(query: GetReviewIdsForSelectionQuery, options?: EndpointOptions) {
        const response = await this._axios.get<PaginatedResponse<ReviewIdRow>>('', {
            params: objectToUrlParams({ ...query, mode: 'selection' }),
            ...this.requestOptions(options),
        });

        return response.data.data.map((review) => review.id);
    }

    getReviewSelectionSession(sessionId: string, options?: EndpointOptions) {
        return this._axios.get<{ ids: string[] }>('/selection', {
            params: objectToUrlParams({
                sessionId,
            }),
            ...this.requestOptions(options),
        });
    }

    getReviewSelectionReconcileStatus(sessionId: string, requestId: string, options?: EndpointOptions) {
        return this._axios.get<{ status: 'pending' | 'completed' | 'stale'; ids: string[]; count: number }>(
            '/selection',
            {
                params: objectToUrlParams({
                    sessionId,
                    requestId,
                }),
                ...this.requestOptions(options),
            },
        );
    }

    selectAllReviewSelectionSession(
        params: { sessionId: string; search?: string[] } & Partial<Sort>,
        options?: EndpointOptions,
    ) {
        return this._axios.post<{ ids: string[] }>(
            '/selection',
            {
                sessionId: params.sessionId,
                search: params.search,
                sortKey: params.sortKey,
                sortOrder: params.sortOrder,
            },
            this.requestOptions(options),
        );
    }

    reconcileReviewSelectionSession(
        params: { sessionId: string; search?: string[]; requestId?: string } & Partial<Sort>,
        options?: EndpointOptions,
    ) {
        return this._axios.put<{ ids: string[] }>(
            '/selection',
            {
                sessionId: params.sessionId,
                search: params.search,
                sortKey: params.sortKey,
                sortOrder: params.sortOrder,
                requestId: params.requestId,
            },
            this.requestOptions(options),
        );
    }

    updateReviewSelectionSession(
        sessionId: string,
        data: {
            addIds?: string[];
            removeIds?: string[];
        },
        options?: EndpointOptions,
    ) {
        return this._axios.patch('/selection', { sessionId, ...data }, this.requestOptions(options));
    }

    clearReviewSelectionSession(sessionId: string, options?: EndpointOptions) {
        return this._axios.delete('/selection', {
            params: { sessionId },
            ...this.requestOptions(options),
        });
    }

    getSelectedReviewsForAnswer(selectionSessionId: string, options?: EndpointOptions) {
        return this._axios.post<MarketplaceReviewSelectionDetails[]>(
            '/selected',
            { sessionId: selectionSessionId },
            this.requestOptions(options),
        );
    }

    createReviewAnswers(data: ReviewAnswerDto[], options?: EndpointOptions) {
        const payload: CreateReviewAnswersPayload = {
            answers: data,
        };
        return this._axios.post<CreateReviewAnswersResponse>(
            '/answer-queue',
            payload,
            this.suppressedRequestOptions(options),
        );
    }

    updateReviewAnswer(data: ReviewAnswerDto, options?: EndpointOptions) {
        return this._axios.patch<ReviewAnswerDto, AxiosResponse<null>>(
            '/answer-queue',
            data,
            this.suppressedRequestOptions(options),
        );
    }

    getHistory(id: string, options?: EndpointOptions) {
        return this._axios.get<ChatMessage[]>(`/${id}/chat-history`, this.requestOptions(options));
    }

    generateAiAnswer(data: GenerateAiAnswerPayload, options?: GenerateAiAnswerOptions) {
        return this._axios.post<GenerateAiAnswerResponse>('/ai-answer', data, this.suppressedRequestOptions(options));
    }

    generateManualAiAnswer(data: GenerateManualAiAnswerPayload, options?: EndpointOptions) {
        return this._axios.post<GenerateAiAnswerResponse>(
            '/ai-answer/manual',
            data,
            this.suppressedRequestOptions(options),
        );
    }

    enqueueAiSelectedAnalysis(data: EnqueueAiSelectedAnalysisPayload, options?: EndpointOptions) {
        return this._axios.post<EnqueueAiSelectedAnalysisResponse>(
            '/ai-analysis-queue',
            data,
            this.suppressedRequestOptions(options),
        );
    }

    getAiSelectedAnalysisProgress(operationId: string, options?: EndpointOptions) {
        return this._axios.get<AiSelectedAnalysisProgressSnapshotResponse>('/ai-analysis-progress', {
            params: objectToUrlParams({ operationId }),
            ...this.suppressedRequestOptions(options),
        });
    }

    getAnswerTemplates(options?: EndpointOptions) {
        return this._axios.get<ReviewAnswerTemplateEntity[]>('/answer-templates', this.requestOptions(options));
    }

    createAnswerTemplate(data: AnswerTemplateUpsertPayload, options?: EndpointOptions) {
        return this._axios.post<ReviewAnswerTemplateEntity>('/answer-templates', data, this.requestOptions(options));
    }

    updateAnswerTemplate(templateId: string, data: AnswerTemplateUpsertPayload, options?: EndpointOptions) {
        return this._axios.patch<ReviewAnswerTemplateEntity>(
            `/answer-templates/${templateId}`,
            data,
            this.requestOptions(options),
        );
    }

    deleteAnswerTemplate(templateId: string, options?: EndpointOptions) {
        return this._axios.delete<{ id: string }>(`/answer-templates/${templateId}`, this.requestOptions(options));
    }

    getAutoReplyScenarios(options?: EndpointOptions) {
        return this._axios.get<AutoReplyScenarioEntity[]>('/auto-replies', this.requestOptions(options));
    }

    enqueueSelectedAutoRepliesRun(data: SelectedAutoRepliesRunPayload, options?: EndpointOptions) {
        return this._axios.post<SelectedAutoRepliesRunResponse>(
            '/auto-replies/selected-run',
            data,
            this.requestOptions(options),
        );
    }

    createAutoReplyScenario(data: AutoReplyScenarioPayload, options?: EndpointOptions) {
        return this._axios.post<AutoReplyScenarioEntity>('/auto-replies', data, this.requestOptions(options));
    }

    updateAutoReplyScenario(scenarioId: string, data: AutoReplyScenarioPayload, options?: EndpointOptions) {
        return this._axios.patch<AutoReplyScenarioEntity>(
            `/auto-replies/${scenarioId}`,
            data,
            this.requestOptions(options),
        );
    }

    deleteAutoReplyScenario(scenarioId: string, options?: EndpointOptions) {
        return this._axios.delete<{ id: string }>(`/auto-replies/${scenarioId}`, this.requestOptions(options));
    }

    reorderAutoReplyScenarios(orderedIds: string[], options?: EndpointOptions) {
        return this._axios.post<AutoReplyScenarioEntity[]>(
            '/auto-replies/reorder',
            { orderedIds },
            this.requestOptions(options),
        );
    }

    getAnswerDraft(reviewFeedItemId: string, options?: EndpointOptions) {
        return this._axios.get<ReviewAnswerDraftEntity | null>(
            `/${reviewFeedItemId}/answer-draft`,
            this.requestOptions(options),
        );
    }

    approveAnswerDraft(reviewFeedItemId: string, data: ApproveReviewAnswerDraftPayload, options?: EndpointOptions) {
        return this._axios.post<ApproveReviewAnswerDraftResponse>(
            `/${reviewFeedItemId}/answer-draft/approve`,
            data,
            this.suppressedRequestOptions(options),
        );
    }

    async downloadAibackReportPdf(payload: AibackReportPdfPayloadEntity, options?: EndpointOptions) {
        const response = await this._axios.post<ArrayBuffer>('/report-pdf', payload, {
            ...this.requestOptions(options),
            responseType: 'arraybuffer',
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        triggerBlobDownload(blob, response.headers as Record<string, unknown>, 'aiback-report.pdf');
    }

    async downloadCsv(query: DownloadCsvQuery, options?: EndpointOptions): Promise<DownloadCsvResult> {
        try {
            const response = await this._axios.post<Blob>('/download-csv', query, {
                ...this.requestOptions(options),
                responseType: 'blob',
                validateStatus: (status) => status < 500,
            });

            const unsupportedPayload = await parseUnsupportedMarketplaceAxiosBlobResponse({
                status: response.status,
                headers: response.headers,
                data: response.data,
            });
            if (unsupportedPayload) {
                throw new UnsupportedMarketplaceRouteError(unsupportedPayload);
            }

            if (response.status >= 400) {
                throw await createRepositoryApiErrorFromResponse({
                    status: response.status,
                    payload: response.data,
                    headers: response.headers,
                    fallbackMessage: `Failed to download reviews CSV (HTTP ${response.status})`,
                    fallbackCode: 'reviews_feed.transport.csv_download_failed',
                    fallbackCategory: 'transport',
                });
            }

            const headers = response.headers as Record<string, unknown>;
            const limitWarning = extractSelectionLimitWarning(headers);
            triggerBlobDownload(response.data, headers, 'reviews.csv');

            return { limitWarning };
        } catch (error) {
            throw await normalizeRepositoryError({
                error,
                fallbackMessage: 'Failed to download reviews CSV',
                fallbackCode: 'reviews_feed.transport.csv_download_failed',
                fallbackCategory: 'transport',
            });
        }
    }
}
